import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { IconButton, Tooltip, useAlert, useDialog } from "@digital-services/ui";

import { OrganizationUser } from "@organization/models/OrganizationUser";
import { useRemoveOrganizationUsers } from "@users/organizationuser/services/OrganizationUserService";

import TrashIcon from "@icons/trash.svg?react";
import { defaultTriggerDialogOptions } from "@utils/common";

interface Props {
  users: OrganizationUser[];
  onSuccess: () => void;
}

const DeleteOrganizationUserButton: FunctionComponent<Props> = props => {
  const { users, onSuccess } = props;

  const { showDialog } = useDialog();
  const { notifyError, notifySuccess } = useAlert();

  const { t } = useTranslation(undefined, { keyPrefix: "organization.users" });
  const { t: tCommon } = useTranslation();

  const { deleteUser: deleteOrgUser } = useRemoveOrganizationUsers();

  const removeUsers = async () => {
    for (const user of users) {
      const { error } = await deleteOrgUser(user.id);

      if (error) {
        notifyError({
          header: t("delete.error.message", { user: user.displayName }),
          "data-testid": "delete-user-from-organization-error"
        });
      } else {
        notifySuccess({
          header: t("delete.success.message", { user: user.displayName }),
          "data-testid": "delete-user-from-organization-success"
        });
        onSuccess();
      }
    }
  };

  const handleDelete = () => {
    showDialog({
      variant: "warning",
      header: t("delete.confirm.title"),
      content: t("delete.confirm.message"),
      confirmButton: tCommon("button.confirm.label"),
      onConfirm: async () => {
        await removeUsers();
      },
      cancelButton: tCommon("button.cancel.label"),
      "data-testid": "delete-user",
      ...defaultTriggerDialogOptions
    });
  };

  return (
    <Tooltip message={t("delete.tooltip")}>
      <IconButton onClick={handleDelete} data-testid="delete-icon-button">
        <TrashIcon className="h-3 sm:h-4" />
      </IconButton>
    </Tooltip>
  );
};

export default DeleteOrganizationUserButton;
